import { useState, useRef, useEffect } from "react";
import moment from "moment";
import { Link, useOutletContext } from "react-router-dom";
import df from "../../images/Default_pfp.jpg";

function PostCard({ post, limit = 100, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [liked, setLiked] = useState(post?.liked);
  const [likesCount, setLikesCount] = useState(post?.likes_count || 0);
  const { user, token } = useOutletContext();
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(post?.status || "pending");
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (post?.status) {
      setCurrentStatus(post.status);
    }
  }, [post?.status]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  if (!post || deleted) return null;

  const isOwner = (post.user?.id === user?.id) || (post.user_id === user?.id);
  const isAdmin = user?.role === "admin";
  const canDelete =
    isAdmin ||
    user?.role === "faculty" ||
    isOwner;

  const handleUpdateStatus = (newStatus) => {
    setUpdatingStatus(true);
    fetch("/api/admin/update-post-status", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ post_id: post.id, status: newStatus }),
    })
      .then((res) => res.json())
      .then((data) => {
        setUpdatingStatus(false);
        setShowMenu(false);
        if (data.post) {
          setCurrentStatus(newStatus);
        } else if (data.error) {
          alert(data.error);
        }
      })
      .catch((err) => {
        console.error(err);
        setUpdatingStatus(false);
        alert("Failed to update post status");
      });
  };

  const handleDeletePost = () => {
    if (!window.confirm("Are you sure you want to delete this post?")) {
      return;
    }
    setDeleting(true);
    fetch('/api/post-delete', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ post_id: post.id })
    })
      .then((response) => response.json())
      .then((data) => {
        setDeleting(false);
        setShowMenu(false);
        if (data.message) {
          setDeleted(true);
          if (onDelete) {
            onDelete(post.id);
          }
        } else if (data.error) {
          alert(data.error);
        }
      })
      .catch((error) => {
        console.error("Delete post error:", error);
        setDeleting(false);
        alert("Failed to delete post");
      });
  };

  const isLong = post.content?.length > limit;
  const displayedText = expanded || !isLong
    ? post.content
    : post.content.substring(0, limit) + "...";

  const postLike = () => {
    fetch('/api/post-like', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ post_id: post.id })
    }).then((response) => response.json())
      .then((data) => {
        if (data.data == false) {
          setLiked(false);
          setLikesCount(likesCount - 1);
        } else {
          setLiked(true);
          setLikesCount(likesCount + 1);
        }
      }).catch((error) => {
        console.log(error);
      });
  };

  return (
    <div className="card w-100 shadow-xss rounded-xxl border-0 p-4 my-2">
      <div className="card-body p-0 d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex align-items-center">
          <figure className="avatar me-3 mb-0">
            <Link to={post.user?.id === user?.id ? "/profile" : `/profile/${post.user?.id}`}>
              <img
                src={post.user?.personal_details?.profile_picture || df}
                alt="user"
                className="shadow-sm rounded-circle w45"
              />
            </Link>
          </figure>
          <Link to={post.user?.id === user?.id ? "/profile" : `/profile/${post.user?.id}`} className="text-decoration-none">
            <h4 className="fw-700  font-xssss mt-1 mb-0 d-flex align-items-center flex-wrap gap-1">
              <span>{post.user?.name ?? "Unknown User"}</span>
              {/* Status pill beside post */}
              {currentStatus === "pending" && (
                <span className="badge bg-warning text-dark font-xsssss px-2 py-1 rounded-pill fw-600">
                  Pending
                </span>
              )}
              {currentStatus === "banned" && (
                <span className="badge bg-danger text-white font-xsssss px-2 py-1 rounded-pill fw-600">
                  Banned
                </span>
              )}
              {currentStatus === "approved" && isOwner && (
                <span className="badge bg-success text-white font-xsssss px-2 py-1 rounded-pill fw-600">
                  Approved
                </span>
              )}
              <span className="d-block w-100 font-xssss fw-500 mt-1 lh-3 ">
                {post.created_at ? moment(post.created_at).fromNow() : ""}
              </span>
            </h4>
          </Link>
        </div>

        {(canDelete || isAdmin) && (
          <div className="position-relative ms-auto" ref={menuRef}>
            <button
              type="button"
              className="btn btn-round-sm bg-greylight text-grey-600 border-0 d-flex align-items-center justify-content-center p-0"
              style={{ width: 34, height: 34, cursor: "pointer" }}
              onClick={() => setShowMenu(!showMenu)}
              title="Options"
            >
              <i className="feather-more-horizontal font-sm"></i>
            </button>
            {showMenu && (
              <div
                className="dropdown-menu show p-2 rounded-3 border-0 shadow-lg position-absolute end-0 mt-1"
                style={{ minWidth: 160, zIndex: 100 }}
              >
                {isAdmin && (
                  <>
                    {currentStatus !== "approved" && (
                      <button
                        type="button"
                        className="dropdown-item d-flex align-items-center text-success font-xssss fw-600 p-2 rounded-2 border-0 bg-transparent w-100"
                        onClick={() => handleUpdateStatus("approved")}
                        disabled={updatingStatus}
                      >
                        <i className="feather-check-circle font-xs me-2"></i>
                        Approve Post
                      </button>
                    )}
                    {currentStatus !== "banned" && (
                      <button
                        type="button"
                        className="dropdown-item d-flex align-items-center text-warning font-xssss fw-600 p-2 rounded-2 border-0 bg-transparent w-100"
                        onClick={() => handleUpdateStatus("banned")}
                        disabled={updatingStatus}
                      >
                        <i className="feather-slash font-xs me-2"></i>
                        Ban Post
                      </button>
                    )}
                    <div className="dropdown-divider my-1"></div>
                  </>
                )}
                <button
                  type="button"
                  className="dropdown-item d-flex align-items-center text-danger font-xssss fw-600 p-2 rounded-2 border-0 bg-transparent w-100"
                  onClick={handleDeletePost}
                  disabled={deleting}
                >
                  <i className="feather-trash-2 font-xs me-2"></i>
                  {deleting ? "Deleting..." : "Delete Post"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="card-body p-0 me-lg-5">
        <p className="fw-500 lh-26 font-xssss w-100">
          {displayedText}{" "}
          {isLong && (
            <button
              type="button"
              className="fw-600 text-primary ms-2 bg-transparent border-0 p-0"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? "See less" : "See more"}
            </button>
          )}
        </p>
      </div>

      {/* Images */}
      {post.images?.length > 0 && (
        <div className="card-body d-block p-0 mb-3">
          {/* First row (2 images) */}
          <div className="row ps-2 pe-2 justify-content-center align-items-center">
            {post.images.slice(0, 2).map((img, index) => (
              <div key={index} className="col-xs-6 col-sm-6 p-1" onClick={() => setShowInfoModal(true)}>

                <img
                  src={img.image_path}
                  className="rounded-3 w-100 cursor-pointer"
                  alt={`post-${post.id}-img-${index}`}
                />

              </div>
            ))}
          </div>

          {/* Second row (next 3 images) */}
          {post.images.length > 2 && (
            <div className="row ps-2 pe-2 justify-content-center align-items-center">
              {post.images.slice(2, 5).map((img, index) => {
                const globalIndex = index + 2; // actual index in images array
                const isLast = globalIndex === 4 && post.images.length > 5;
                return (

                  <div key={globalIndex} className="col-xs-4 col-sm-4 p-1" onClick={() => setShowInfoModal(true)}>

                    <img
                      src={img.image_path}
                      className="rounded-3 w-100 object-fit-cover  h-200px cursor-pointer"
                      alt={`post-${post.id}-img-${globalIndex}`}
                    />
                    {isLast && (
                      <span className="img-count font-sm text-white ls-3 fw-600">
                        <b>+{post.images.length - 5}</b>
                      </span>
                    )}

                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="card-body d-flex p-0">
        <a
          onClick={postLike}
          className="emoji-bttn  d-flex align-items-center justify-content-center fw-600 text-grey-900 text-dark lh-26 font-xssss me-2"
        >
          <i className={liked == true ? "feather-thumbs-up text-white bg-primary-gradiant me-1 btn-round-xs font-xss" : "feather-thumbs-up me-1 btn-round-xs font-xss"}></i>
          {/* text-white bg-primary-gradiant add this above */}
          {likesCount !== 0 && likesCount} Like
        </a>




      </div>
      {/* Info Modal */}
      {showInfoModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
          onClick={() => setShowInfoModal(false)}
        >
          <div
            className="modal-dialog modal-dialog-scrollable"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowInfoModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="carousel slide-carousel" data-bs-ride="carousel">
                  <div className="carousel-inner">
                    {post.images.map((img, index) => (
                      <div key={index} className={`carousel-item ${index === 0 ? 'active' : ''}`}>
                        <img
                          src={img.image_path}
                          alt=""
                          className="d-block w-100 rounded cursor-pointer"
                        />
                      </div>
                    ))}
                  </div>
                  <button className="carousel-control-prev" type="button" data-bs-target=".carousel" data-bs-slide="prev">
                    <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                    <span className="visually-hidden">Previous</span>
                  </button>
                  <button className="carousel-control-next" type="button" data-bs-target=".carousel" data-bs-slide="next">
                    <span className="carousel-control-next-icon" aria-hidden="true"></span>
                    <span className="visually-hidden">Next</span>
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowInfoModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default PostCard;